/**
 * Partner Hub — referral attribution capture & persistence.
 *
 * `?ref=sarah-jones` on any public page is remembered on the visitor's device for 90 days (localStorage),
 * so a buyer who scans a realtor's QR today and books a call next week is still credited to that realtor.
 * A fresh `?ref=` click always wins over the stored value. The backend never overwrites a realtor already
 * tagged on a lead, so remembering generously here is safe.
 */

const STORAGE_KEY = "hm_partner_ref_v2";
const LEGACY_SESSION_KEY = "hm_partner_ref_v1";
export const REF_TTL_MS = 90 * 24 * 60 * 60 * 1000;

function safeStorage(kind) {
  if (typeof window === "undefined") return null;
  try {
    const s = kind === "session" ? window.sessionStorage : window.localStorage;
    const probe = "__hm_probe__";
    s.setItem(probe, probe);
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function readStored() {
  const ls = safeStorage("local");
  if (ls) {
    try {
      const raw = ls.getItem(STORAGE_KEY);
      if (raw) {
        const { slug, exp } = JSON.parse(raw);
        if (slug && exp && Date.now() < exp) return String(slug).trim().toLowerCase();
        ls.removeItem(STORAGE_KEY);
      }
    } catch {
      /* corrupt value — ignore */
    }
  }
  const ss = safeStorage("session");
  if (ss) {
    try {
      const legacy = ss.getItem(LEGACY_SESSION_KEY);
      if (legacy) return legacy.trim().toLowerCase();
    } catch {
      /* no-op */
    }
  }
  return null;
}

function store(slug) {
  const ls = safeStorage("local");
  if (ls) {
    try {
      ls.setItem(STORAGE_KEY, JSON.stringify({ slug, exp: Date.now() + REF_TTL_MS }));
      return;
    } catch {
      /* quota — fall through to session */
    }
  }
  const ss = safeStorage("session");
  if (ss) {
    try { ss.setItem(LEGACY_SESSION_KEY, slug); } catch { /* no-op */ }
  }
}

/** Capture `?ref=slug` from the current URL (90-day memory). Idempotent — call on every page mount.
 * Returns the active slug (newly captured or previously stored) or null. */
export function captureRefFromUrl() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const ref = (params.get("ref") || "").trim().toLowerCase();
  if (ref) {
    store(ref);
    return ref;
  }
  return getPartnerSlug();
}

/** Retrieve the active partner slug, or null when none is remembered (or it expired). */
export function getPartnerSlug() {
  return readStored();
}

/** Clear stored attribution. Called by admin/test surfaces only. */
export function clearPartnerSlug() {
  const ls = safeStorage("local");
  if (ls) { try { ls.removeItem(STORAGE_KEY); } catch { /* no-op */ } }
  const ss = safeStorage("session");
  if (ss) { try { ss.removeItem(LEGACY_SESSION_KEY); } catch { /* no-op */ } }
}

/** Append `?ref=<slug>` to an internal path when an attribution slug is active.
 * Pure when no slug; idempotent when the path already carries `ref=`; hash-safe. */
export function withRef(path) {
  if (!path || typeof path !== "string") return path;
  const slug = getPartnerSlug();
  if (!slug) return path;
  const hashIdx = path.indexOf("#");
  const base = hashIdx >= 0 ? path.slice(0, hashIdx) : path;
  const hash = hashIdx >= 0 ? path.slice(hashIdx) : "";
  if (/[?&]ref=/.test(base)) return path;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}ref=${encodeURIComponent(slug)}${hash}`;
}
